import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaUsers,
  FaMoneyBillWave,
  FaChartLine,
  FaTrophy,
  FaFutbol,
  FaSyncAlt,
  FaUser,
  FaChevronDown,
  FaChevronUp,
  FaCalendarAlt,
  FaLayerGroup,
} from "react-icons/fa";

import "./TotalGain.css";


/* =========================================================
   SETTINGS
========================================================= */

const CACHE_TIME =
  2 * 60 * 1000;

const MAX_LEVEL =
  3;

const getCacheKey = (
  username
) =>
  `memberGainCache_${username}`;


/* =========================================================
   DATE HELPERS
========================================================= */

const getDateValue = (
  value
) => {

  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  try {

    /*
     * JavaScript Date
     */

    if (
      value instanceof Date
    ) {

      return Number.isNaN(
        value.getTime()
      )
        ? null
        : value;

    }


    /*
     * Firestore Timestamp
     */

    if (
      typeof value?.toDate ===
      "function"
    ) {

      const date =
        value.toDate();

      return (
        date instanceof Date &&
        !Number.isNaN(
          date.getTime()
        )
      )
        ? date
        : null;

    }


    /*
     * Serialized Firestore Timestamp
     */

    if (
      typeof value ===
        "object" &&
      typeof value.seconds ===
        "number"
    ) {

      const date =
        new Date(
          value.seconds *
            1000 +
            Math.floor(
              Number(
                value.nanoseconds ||
                  0
              ) / 1000000
            )
        );

      return Number.isNaN(
        date.getTime()
      )
        ? null
        : date;

    }


    /*
     * Another Firebase timestamp format
     */

    if (
      typeof value ===
        "object" &&
      typeof value._seconds ===
        "number"
    ) {

      const date =
        new Date(
          value._seconds *
            1000
        );

      return Number.isNaN(
        date.getTime()
      )
        ? null
        : date;

    }


    /*
     * Number / timestamp
     */

    if (
      typeof value ===
      "number"
    ) {

      const date =
        new Date(value);

      return Number.isNaN(
        date.getTime()
      )
        ? null
        : date;

    }


    /*
     * String
     */

    if (
      typeof value ===
      "string"
    ) {

      const text =
        value.trim();

      if (!text) {
        return null;
      }

      const date =
        new Date(text);

      return Number.isNaN(
        date.getTime()
      )
        ? null
        : date;

    }

    return null;

  } catch (error) {

    console.error(
      "Date conversion error:",
      error
    );

    return null;

  }

};


/* =========================================================
   SAFE NUMBER
========================================================= */

const safeNumber = (
  value
) => {

  const number =
    Number(value);

  return Number.isFinite(
    number
  )
    ? number
    : 0;

};


/* =========================================================
   DATE START
========================================================= */

const startOfDay = (
  date
) => {

  const result =
    new Date(date);

  result.setHours(
    0,
    0,
    0,
    0
  );

  return result;

};


/* =========================================================
   DATE END
========================================================= */

const endOfDay = (
  date
) => {

  const result =
    new Date(date);

  result.setHours(
    23,
    59,
    59,
    999
  );

  return result;

};


/* =========================================================
   TODAY RANGE
========================================================= */

const getTodayRange = () => {

  const now =
    new Date();

  return {
    start:
      startOfDay(now),

    end:
      endOfDay(now),
  };

};


/* =========================================================
   WEEK RANGE
========================================================= */

const getWeekRange = () => {

  const now =
    new Date();

  const day =
    now.getDay();

  /*
   * Monday = first day
   */

  const mondayOffset =
    day === 0
      ? 6
      : day - 1;

  const start =
    new Date(now);

  start.setDate(
    now.getDate() -
      mondayOffset
  );

  return {
    start:
      startOfDay(start),

    end:
      endOfDay(now),
  };

};


/* =========================================================
   MONTH RANGE
========================================================= */

const getMonthRange = () => {

  const now =
    new Date();

  const start =
    new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );

  return {
    start:
      startOfDay(start),

    end:
      endOfDay(now),
  };

};


/* =========================================================
   LAST MONTH RANGE
========================================================= */

const getLastMonthRange = () => {

  const now =
    new Date();

  const start =
    new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      1
    );

  const end =
    new Date(
      now.getFullYear(),
      now.getMonth(),
      0
    );

  return {
    start:
      startOfDay(start),

    end:
      endOfDay(end),
  };

};


/* =========================================================
   DATE IN RANGE
========================================================= */

const isDateInRange = (
  value,
  range
) => {

  const date =
    getDateValue(value);

  if (!date) {
    return false;
  }

  const time =
    date.getTime();

  return (
    time >=
      range.start.getTime() &&
    time <=
      range.end.getTime()
  );

};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (
  value
) => {

  const number =
    safeNumber(value);

  return number.toLocaleString();

};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (
  value
) => {

  const date =
    getDateValue(value);

  if (!date) {
    return "N/A";
  }

  return date.toLocaleDateString(
    [],
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );

};


/* =========================================================
   GET PROFIT
========================================================= */

const getProfit = (
  bet
) => {

  return safeNumber(
    bet?.profit
  );

};


/* =========================================================
   GET TOTAL WIN
========================================================= */

const getTotalWin = (
  bet
) => {

  return safeNumber(
    bet?.totalWin
  );

};


/* =========================================================
   GET STAKE
========================================================= */

const getStake = (
  bet
) => {

  return safeNumber(
    bet?.stake ??
    bet?.amount ??
    bet?.quantity ??
    0
  );

};


/* =========================================================
   MEMBER GAIN
========================================================= */

const MemberGain = () => {

  const navigate =
    useNavigate();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    levels,
    setLevels,
  ] = useState({
    1: [],
    2: [],
    3: [],
  });


  const [
    activeLevel,
    setActiveLevel,
  ] = useState("all");


  const [
    period,
    setPeriod,
  ] = useState("today");


  const [
    expandedUser,
    setExpandedUser,
  ] = useState(null);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    refreshing,
    setRefreshing,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const loggedInUsername =
    localStorage.getItem(
      "username"
    );


  /* =======================================================
     CACHE
  ======================================================= */

  const saveCache =
    useCallback(
      (data) => {

        if (
          !loggedInUsername
        ) {
          return;
        }

        try {

          localStorage.setItem(
            getCacheKey(
              loggedInUsername
            ),
            JSON.stringify({
              data,
              updatedAt:
                Date.now(),
            })
          );

        } catch (cacheError) {

          console.error(
            "Member Gain cache error:",
            cacheError
          );

        }

      },
      [
        loggedInUsername,
      ]
    );


  const loadCache =
    useCallback(
      () => {

        if (
          !loggedInUsername
        ) {
          return null;
        }

        try {

          const raw =
            localStorage.getItem(
              getCacheKey(
                loggedInUsername
              )
            );

          if (!raw) {
            return null;
          }

          return JSON.parse(
            raw
          );

        } catch (cacheError) {

          console.error(
            "Member Gain cache load error:",
            cacheError
          );

          return null;

        }

      },
      [
        loggedInUsername,
      ]
    );


  /* =======================================================
     CURRENT USER
  ======================================================= */

  const fetchCurrentUser =
    useCallback(
      async () => {

        if (
          !loggedInUsername
        ) {
          return null;
        }

        const q =
          query(
            collection(
              db,
              "users"
            ),
            where(
              "username",
              "==",
              loggedInUsername
            ),
            limit(1)
          );

        const snap =
          await getDocs(q);

        if (
          snap.empty
        ) {
          return null;
        }

        return {
          id:
            snap.docs[0].id,

          ...snap.docs[0].data(),
        };

      },
      [
        loggedInUsername,
      ]
    );


  /* =======================================================
     FETCH USER BETS
  ======================================================= */

  const fetchUserBets =
    useCallback(
      async (
        username
      ) => {

        if (!username) {
          return [];
        }

        try {

          const q =
            query(
              collection(
                db,
                "antscore"
              ),
              where(
                "username",
                "==",
                username
              )
            );

          const snap =
            await getDocs(q);

          const bets = [];


          snap.forEach(
            (doc) => {

              const data =
                doc.data();

              const createdAt =
                getDateValue(
                  data.createdAt
                );

              /*
               * Ignore invalid dates
               */

              if (!createdAt) {
                return;
              }


              bets.push({

                id:
                  doc.id,

                betId:
                  data.betId ||
                  doc.id,

                username:
                  data.username ||
                  username,

                createdAt,

                amount:
                  safeNumber(
                    data.amount ??
                    data.stake ??
                    data.quantity ??
                    0
                  ),

                stake:
                  safeNumber(
                    data.stake ??
                    data.amount ??
                    data.quantity ??
                    0
                  ),

                totalWin:
                  safeNumber(
                    data.totalWin ??
                    0
                  ),

                profit:
                  safeNumber(
                    data.profit ??
                    0
                  ),

                currency:
                  data.currency ||
                  "TZS",

                result:
                  data.result ||
                  "",

                status:
                  data.status ||
                  "",

                league:
                  data.league ||
                  "N/A",

                teamA:
                  data.teamA ||
                  "N/A",

                teamB:
                  data.teamB ||
                  "N/A",

                odds:
                  safeNumber(
                    data.odds
                  ),

              });

            }
          );


          bets.sort(
            (
              a,
              b
            ) =>
              b.createdAt.getTime() -
              a.createdAt.getTime()
          );


          return bets;

        } catch (fetchError) {

          console.error(
            `Failed fetching bets for ${username}:`,
            fetchError
          );

          return [];

        }

      },
      []
    );


  /* =======================================================
     FETCH LEVEL USERS
  ======================================================= */

  const fetchLevelUsers =
    useCallback(
      async (
        referralCodes,
        maxLevel = 3
      ) => {

        const result = {
          1: [],
          2: [],
          3: [],
        };


        let currentCodes =
          Array.isArray(
            referralCodes
          )
            ? referralCodes
            : [];


        let level = 1;


        /*
         * Prevent duplicate referral codes
         */

        currentCodes =
          Array.from(
            new Set(
              currentCodes.filter(
                Boolean
              )
            )
          );


        while (
          level <= maxLevel &&
          currentCodes.length > 0
        ) {

          const chunks = [];


          /*
           * Firestore "in"
           * supports max 10 values.
           */

          for (
            let i = 0;
            i <
            currentCodes.length;
            i += 10
          ) {

            chunks.push(
              currentCodes.slice(
                i,
                i + 10
              )
            );

          }


          const snapshots =
            await Promise.all(
              chunks.map(
                async (
                  chunk
                ) => {

                  try {

                    const q =
                      query(
                        collection(
                          db,
                          "users"
                        ),
                        where(
                          "whoReferredWho",
                          "in",
                          chunk
                        )
                      );

                    return await getDocs(
                      q
                    );

                  } catch (
                    levelError
                  ) {

                    console.error(
                      "Level fetch error:",
                      levelError
                    );

                    return null;

                  }

                }
              )
            );


          let users = [];


          snapshots.forEach(
            (snap) => {

              if (!snap) {
                return;
              }

              snap.forEach(
                (doc) => {

                  users.push({
                    id:
                      doc.id,

                    ...doc.data(),
                  });

                }
              );

            }
          );


          /*
           * Remove duplicate users.
           */

          users =
            Array.from(
              new Map(
                users.map(
                  (user) => [
                    user.id,
                    user,
                  ]
                )
              ).values()
            );


          /*
           * Fetch all bets for
           * each member.
           */

          const usersWithBets =
            await Promise.all(
              users.map(
                async (
                  user
                ) => {

                  const bets =
                    await fetchUserBets(
                      user.username
                    );


                  return {

                    ...user,

                    bets,

                  };

                }
              )
            );


          /*
           * Only users with
           * betting activity are
           * shown.
           */

          result[level] =
            usersWithBets.filter(
              (user) =>
                user.bets &&
                user.bets.length >
                  0
            );


          /*
           * Continue to next
           * referral level.
           */

          currentCodes =
            Array.from(
              new Set(
                users
                  .map(
                    (user) =>
                      user.referralCode
                  )
                  .filter(Boolean)
              )
            );


          level++;

        }


        return result;

      },
      [
        fetchUserBets,
      ]
    );


  /* =======================================================
     LOAD DATA
  ======================================================= */

  const loadMemberGain =
    useCallback(
      async (
        showRefresh = false
      ) => {

        if (
          !loggedInUsername
        ) {

          setLoading(false);

          return;

        }


        if (
          showRefresh
        ) {

          setRefreshing(true);

        }


        setError("");


        try {

          const user =
            await fetchCurrentUser();


          if (
            !user?.referralCode
          ) {

            const empty = {
              1: [],
              2: [],
              3: [],
            };

            setLevels(
              empty
            );

            saveCache(
              empty
            );

            return;

          }


          const data =
            await fetchLevelUsers(
              [
                user.referralCode,
              ],
              MAX_LEVEL
            );


          setLevels(
            data
          );


          saveCache(
            data
          );

        } catch (
          loadError
        ) {

          console.error(
            "Member Gain error:",
            loadError
          );

          setError(
            "Failed to load member gains."
          );

        } finally {

          setLoading(false);

          setRefreshing(false);

        }

      },
      [
        loggedInUsername,
        fetchCurrentUser,
        fetchLevelUsers,
        saveCache,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(
    () => {

      if (
        !loggedInUsername
      ) {

        setLoading(false);

        return;

      }


      const cached =
        loadCache();


      if (
        cached?.data
      ) {

        setLevels({
          1:
            cached.data[1] ||
            [],

          2:
            cached.data[2] ||
            [],

          3:
            cached.data[3] ||
            [],
        });


        setLoading(
          false
        );

      }


      loadMemberGain(
        false
      );

    },
    [
      loggedInUsername,
      loadCache,
      loadMemberGain,
    ]
  );


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {

      if (
        !loggedInUsername
      ) {
        return undefined;
      }


      const interval =
        setInterval(
          () => {

            loadMemberGain(
              false
            );

          },
          CACHE_TIME
        );


      return () => {

        clearInterval(
          interval
        );

      };

    },
    [
      loggedInUsername,
      loadMemberGain,
    ]
  );


  /* =======================================================
     DATE RANGE
  ======================================================= */

  const selectedRange =
    useMemo(
      () => {

        if (
          period ===
          "today"
        ) {

          return getTodayRange();

        }

        if (
          period ===
          "week"
        ) {

          return getWeekRange();

        }

        if (
          period ===
          "month"
        ) {

          return getMonthRange();

        }

        return getLastMonthRange();

      },
      [
        period,
      ]
    );


  /* =======================================================
     ACTIVE USERS
  ======================================================= */

  const activeUsers =
    useMemo(
      () => {

        if (
          activeLevel ===
          "all"
        ) {

          return [
            ...(levels[1] || []),
            ...(levels[2] || []),
            ...(levels[3] || []),
          ];

        }

        return (
          levels[
            Number(
              activeLevel
            )
          ] || []
        );

      },
      [
        levels,
        activeLevel,
      ]
    );


  /* =======================================================
     USER STATS
  ======================================================= */

  const getUserPeriodStats =
    useCallback(
      (
        user
      ) => {

        const bets =
          Array.isArray(
            user.bets
          )
            ? user.bets
            : [];


        const periodBets =
          bets.filter(
            (bet) =>
              isDateInRange(
                bet.createdAt,
                selectedRange
              )
          );


        const totalWin =
          periodBets.reduce(
            (
              total,
              bet
            ) =>
              total +
              getTotalWin(
                bet
              ),
            0
          );


        const profit =
          periodBets.reduce(
            (
              total,
              bet
            ) =>
              total +
              getProfit(
                bet
              ),
            0
          );


        const stake =
          periodBets.reduce(
            (
              total,
              bet
            ) =>
              total +
              getStake(
                bet
              ),
            0
          );


        return {

          bets:
            periodBets.length,

          totalWin,

          profit,

          stake,

        };

      },
      [
        selectedRange,
      ]
    );


  /* =======================================================
     LEVEL STATS
  ======================================================= */

  const levelStats =
    useMemo(
      () => {

        const stats = {
          1: {
            users:
              levels[1]?.length ||
              0,

            bets: 0,

            stake: 0,

            totalWin: 0,

            profit: 0,
          },

          2: {
            users:
              levels[2]?.length ||
              0,

            bets: 0,

            stake: 0,

            totalWin: 0,

            profit: 0,
          },

          3: {
            users:
              levels[3]?.length ||
              0,

            bets: 0,

            stake: 0,

            totalWin: 0,

            profit: 0,
          },
        };


        [1, 2, 3].forEach(
          (level) => {

            const users =
              levels[level] ||
              [];


            users.forEach(
              (user) => {

                const userStats =
                  getUserPeriodStats(
                    user
                  );


                stats[level].bets +=
                  userStats.bets;


                stats[level].stake +=
                  userStats.stake;


                stats[level].totalWin +=
                  userStats.totalWin;


                stats[level].profit +=
                  userStats.profit;

              }
            );

          }
        );


        return stats;

      },
      [
        levels,
        getUserPeriodStats,
      ]
    );


  /* =======================================================
     ALL TOTAL
  ======================================================= */

  const allStats =
    useMemo(
      () => {

        return [1, 2, 3].reduce(
          (
            total,
            level
          ) => {

            total.users +=
              levelStats[level]
                .users;

            total.bets +=
              levelStats[level]
                .bets;

            total.stake +=
              levelStats[level]
                .stake;

            total.totalWin +=
              levelStats[level]
                .totalWin;

            total.profit +=
              levelStats[level]
                .profit;

            return total;

          },
          {
            users: 0,
            bets: 0,
            stake: 0,
            totalWin: 0,
            profit: 0,
          }
        );

      },
      [
        levelStats,
      ]
    );


  /* =======================================================
     ACTIVE SUMMARY
  ======================================================= */

  const activeSummary =
    useMemo(
      () => {

        if (
          activeLevel ===
          "all"
        ) {

          return allStats;

        }

        return (
          levelStats[
            Number(
              activeLevel
            )
          ] || {
            users: 0,
            bets: 0,
            stake: 0,
            totalWin: 0,
            profit: 0,
          }
        );

      },
      [
        activeLevel,
        allStats,
        levelStats,
      ]
    );


  /* =======================================================
     PERIOD LABEL
  ======================================================= */

  const periodLabel =
    useMemo(
      () => {

        if (
          period ===
          "today"
        ) {
          return "Today";
        }

        if (
          period ===
          "week"
        ) {
          return "This Week";
        }

        if (
          period ===
          "month"
        ) {
          return "This Month";
        }

        return "Last Month";

      },
      [
        period,
      ]
    );


  /* =======================================================
     LOGIN
  ======================================================= */

  if (
    !loggedInUsername
  ) {

    return (

      <div className="member-gain-wrapper">

        <div className="member-gain-empty">

          <div className="member-gain-empty-icon">
            <FaUser />
          </div>

          <h3>
            Login Required
          </h3>

          <p>
            Please login first to
            view member gains.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate(-1)
            }
          >
            <FaArrowLeft />

            Back
          </button>

        </div>

      </div>

    );

  }


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="member-gain-wrapper">

        <div className="member-gain-loading">

          <div className="member-gain-spinner" />

          <strong>
            Loading Member Gain
          </strong>

          <span>
            Calculating team earnings...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="member-gain-wrapper">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="member-gain-header">

        <button
          type="button"
          className="member-gain-back"
          onClick={() =>
            navigate(-1)
          }
        >

          <FaArrowLeft />

          <span>
            Back
          </span>

        </button>


        <div className="member-gain-title">

          <div className="member-gain-title-icon">

            <FaChartLine />

          </div>

          <div>

            <span>
              TEAM PERFORMANCE
            </span>

            <h1>
              Member Gain
            </h1>

          </div>

        </div>


        <button
          type="button"
          className={
            refreshing
              ? "member-gain-refresh spinning"
              : "member-gain-refresh"
          }
          onClick={() =>
            loadMemberGain(
              true
            )
          }
          title="Refresh"
        >

          <FaSyncAlt />

        </button>

      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div className="member-gain-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              loadMemberGain(
                true
              )
            }
          >
            Retry
          </button>

        </div>

      )}


      {/* =================================================
          PERIOD
      ================================================= */}

      <div className="member-gain-period">

        <div className="member-gain-period-title">

          <FaCalendarAlt />

          <span>
            EARNING PERIOD
          </span>

        </div>


        <div className="member-gain-period-buttons">

          <button
            type="button"
            className={
              period ===
              "today"
                ? "active"
                : ""
            }
            onClick={() =>
              setPeriod(
                "today"
              )
            }
          >
            Today
          </button>


          <button
            type="button"
            className={
              period ===
              "week"
                ? "active"
                : ""
            }
            onClick={() =>
              setPeriod(
                "week"
              )
            }
          >
            This Week
          </button>


          <button
            type="button"
            className={
              period ===
              "month"
                ? "active"
                : ""
            }
            onClick={() =>
              setPeriod(
                "month"
              )
            }
          >
            This Month
          </button>


          <button
            type="button"
            className={
              period ===
              "lastMonth"
                ? "active"
                : ""
            }
            onClick={() =>
              setPeriod(
                "lastMonth"
              )
            }
          >
            Last Month
          </button>

        </div>

      </div>


      {/* =================================================
          MAIN SUMMARY
      ================================================= */}

      <div className="member-gain-main-summary">

        <div className="member-gain-summary-icon">

          <FaMoneyBillWave />

        </div>


        <div className="member-gain-summary-content">

          <span>
            TOTAL WIN —{" "}
            {periodLabel}
          </span>

          <strong>
            {formatNumber(
              activeSummary.totalWin
            )}
          </strong>

          <small>
            TZS
          </small>

        </div>


        <div className="member-gain-summary-profit">

          <span>
            PROFIT
          </span>

          <strong
            className={
              activeSummary.profit <
              0
                ? "negative"
                : "positive"
            }
          >
            {formatNumber(
              activeSummary.profit
            )}
          </strong>

          <small>
            TZS
          </small>

        </div>

      </div>


      {/* =================================================
          STATS
      ================================================= */}

      <div className="member-gain-stats">

        <div className="member-gain-stat">

          <div className="member-gain-stat-icon">

            <FaUsers />

          </div>

          <div>

            <span>
              MEMBERS
            </span>

            <strong>
              {
                activeSummary.users
              }
            </strong>

          </div>

        </div>


        <div className="member-gain-stat">

          <div className="member-gain-stat-icon">

            <FaFutbol />

          </div>

          <div>

            <span>
              BETS
            </span>

            <strong>
              {
                activeSummary.bets
              }
            </strong>

          </div>

        </div>


        <div className="member-gain-stat">

          <div className="member-gain-stat-icon">

            <FaMoneyBillWave />

          </div>

          <div>

            <span>
              STAKE
            </span>

            <strong>
              {formatNumber(
                activeSummary.stake
              )}
            </strong>

          </div>

        </div>


        <div className="member-gain-stat">

          <div className="member-gain-stat-icon">

            <FaTrophy />

          </div>

          <div>

            <span>
              TOTAL WIN
            </span>

            <strong>
              {formatNumber(
                activeSummary.totalWin
              )}
            </strong>

          </div>

        </div>

      </div>


      {/* =================================================
          LEVEL TABS
      ================================================= */}

      <div className="member-gain-level-tabs">

        <button
          type="button"
          className={
            activeLevel ===
            "all"
              ? "active"
              : ""
          }
          onClick={() => {

            setActiveLevel(
              "all"
            );

            setExpandedUser(
              null
            );

          }}
        >

          <FaLayerGroup />

          <span>
            ALL LEVELS
          </span>

          <strong>
            {allStats.users}
          </strong>

        </button>


        {[1, 2, 3].map(
          (level) => (

            <button
              key={level}
              type="button"
              className={
                activeLevel ===
                String(level)
                  ? "active"
                  : ""
              }
              onClick={() => {

                setActiveLevel(
                  String(level)
                );

                setExpandedUser(
                  null
                );

              }}
            >

              <span>
                LEVEL {level}
              </span>

              <strong>
                {
                  levelStats[
                    level
                  ].users
                }
              </strong>

            </button>

          )
        )}

      </div>


      {/* =================================================
          LEVEL SUMMARY CARDS
      ================================================= */}

      <div className="member-gain-level-summary">

        {[1, 2, 3].map(
          (level) => {

            const stats =
              levelStats[
                level
              ];

            return (

              <div
                key={level}
                className={
                  activeLevel ===
                  String(level)
                    ? "member-gain-level-card active"
                    : "member-gain-level-card"
                }
              >

                <div className="member-gain-level-card-head">

                  <div className="member-gain-level-badge">

                    L{level}

                  </div>

                  <div>

                    <span>
                      LEVEL {level}
                    </span>

                    <strong>
                      {stats.users} Members
                    </strong>

                  </div>

                </div>


                <div className="member-gain-level-card-row">

                  <span>
                    Total Win
                  </span>

                  <strong>
                    {formatNumber(
                      stats.totalWin
                    )}
                  </strong>

                </div>


                <div className="member-gain-level-card-row">

                  <span>
                    Profit
                  </span>

                  <strong
                    className={
                      stats.profit <
                      0
                        ? "negative"
                        : "positive"
                    }
                  >
                    {formatNumber(
                      stats.profit
                    )}
                  </strong>

                </div>


                <div className="member-gain-level-card-row">

                  <span>
                    Bets
                  </span>

                  <strong>
                    {stats.bets}
                  </strong>

                </div>

              </div>

            );

          }
        )}

      </div>


      {/* =================================================
          ALL TOTAL
      ================================================= */}

      <div className="member-gain-all-total">

        <div className="member-gain-all-total-head">

          <div className="member-gain-all-total-icon">

            <FaLayerGroup />

          </div>

          <div>

            <span>
              ALL LEVELS TOTAL
            </span>

            <strong>
              {periodLabel}
            </strong>

          </div>

        </div>


        <div className="member-gain-all-total-values">

          <div>

            <span>
              MEMBERS
            </span>

            <strong>
              {allStats.users}
            </strong>

          </div>


          <div>

            <span>
              BETS
            </span>

            <strong>
              {allStats.bets}
            </strong>

          </div>


          <div>

            <span>
              STAKE
            </span>

            <strong>
              {formatNumber(
                allStats.stake
              )}
            </strong>

          </div>


          <div>

            <span>
              TOTAL WIN
            </span>

            <strong>
              {formatNumber(
                allStats.totalWin
              )}
            </strong>

          </div>


          <div>

            <span>
              PROFIT
            </span>

            <strong
              className={
                allStats.profit <
                0
                  ? "negative"
                  : "positive"
              }
            >
              {formatNumber(
                allStats.profit
              )}
            </strong>

          </div>

        </div>

      </div>


      {/* =================================================
          MEMBER LIST HEADER
      ================================================= */}

      <div className="member-gain-section-head">

        <div>

          <span>
            MEMBER PERFORMANCE
          </span>

          <strong>
            {activeLevel ===
            "all"
              ? "All Levels"
              : `Level ${activeLevel}`}
          </strong>

        </div>


        <div className="member-gain-section-period">

          <FaCalendarAlt />

          {periodLabel}

        </div>

      </div>


      {/* =================================================
          MEMBER LIST
      ================================================= */}

      {activeUsers.length ===
      0 ? (

        <div className="member-gain-empty">

          <div className="member-gain-empty-icon">

            <FaUsers />

          </div>

          <h3>
            No Member Data
          </h3>

          <p>
            Hakuna member mwenye
            betting activity katika
            kipindi hiki.
          </p>

        </div>

      ) : (

        <div className="member-gain-list">

          {activeUsers.map(
            (
              user,
              index
            ) => {

              const stats =
                getUserPeriodStats(
                  user
                );


              const isExpanded =
                expandedUser ===
                user.id;


              let userLevel =
                1;


              if (
                levels[2]?.some(
                  (member) =>
                    member.id ===
                    user.id
                )
              ) {

                userLevel = 2;

              }


              if (
                levels[3]?.some(
                  (member) =>
                    member.id ===
                    user.id
                )
              ) {

                userLevel = 3;

              }


              return (

                <div
                  key={
                    user.id
                  }
                  className={
                    isExpanded
                      ? "member-gain-user-card expanded"
                      : "member-gain-user-card"
                  }
                >

                  {/* USER ROW */}

                  <button
                    type="button"
                    className="member-gain-user-row"
                    onClick={() =>
                      setExpandedUser(
                        isExpanded
                          ? null
                          : user.id
                      )
                    }
                  >

                    <div className="member-gain-user-number">

                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}

                    </div>


                    <div className="member-gain-user-avatar">

                      <FaUser />

                    </div>


                    <div className="member-gain-user-info">

                      <strong>
                        {
                          user.username ||
                          "Unknown"
                        }
                      </strong>

                      <span>
                        Level{" "}
                        {userLevel}
                      </span>

                    </div>


                    <div className="member-gain-user-bets">

                      <span>
                        BETS
                      </span>

                      <strong>
                        {stats.bets}
                      </strong>

                    </div>


                    <div className="member-gain-user-win">

                      <span>
                        TOTAL WIN
                      </span>

                      <strong>
                        {formatNumber(
                          stats.totalWin
                        )}
                      </strong>

                    </div>


                    <div className="member-gain-user-profit">

                      <span>
                        PROFIT
                      </span>

                      <strong
                        className={
                          stats.profit <
                          0
                            ? "negative"
                            : "positive"
                        }
                      >
                        {formatNumber(
                          stats.profit
                        )}
                      </strong>

                    </div>


                    <div className="member-gain-user-arrow">

                      {isExpanded ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}

                    </div>

                  </button>


                  {/* DETAILS */}

                  {isExpanded && (

                    <div className="member-gain-user-details">

                      <div className="member-gain-detail">

                        <span>
                          USERNAME
                        </span>

                        <strong>
                          {
                            user.username ||
                            "Unknown"
                          }
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          LEVEL
                        </span>

                        <strong>
                          Level{" "}
                          {userLevel}
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          PERIOD
                        </span>

                        <strong>
                          {periodLabel}
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          BETS
                        </span>

                        <strong>
                          {stats.bets}
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          STAKE
                        </span>

                        <strong>
                          {formatNumber(
                            stats.stake
                          )}{" "}
                          TZS
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          TOTAL WIN
                        </span>

                        <strong>
                          {formatNumber(
                            stats.totalWin
                          )}{" "}
                          TZS
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          PROFIT
                        </span>

                        <strong
                          className={
                            stats.profit <
                            0
                              ? "negative"
                              : "positive"
                          }
                        >
                          {formatNumber(
                            stats.profit
                          )}{" "}
                          TZS
                        </strong>

                      </div>


                      <div className="member-gain-detail">

                        <span>
                          LAST BET
                        </span>

                        <strong>
                          {user.bets?.length
                            ? formatDate(
                                user.bets[0]
                                  .createdAt
                              )
                            : "N/A"}
                        </strong>

                      </div>

                    </div>

                  )}

                </div>

              );

            }
          )}

        </div>

      )}


      {/* =================================================
          FOOTER
      ================================================= */}

      <div className="member-gain-footer">

        <span>
          BFC — MEMBER GAIN
        </span>

        <small>
          Earnings calculated from
          member betting activity.
        </small>

      </div>

    </div>

  );

};


export default MemberGain;
